import { jsx } from "panel";
import kebabCase from "lodash/kebabCase";
import { PropertyType, ResourceType } from "common/report/constants";
import {
  CountBySelection,
  MultiAttributionAggregator,
  RowNumericAggregator,
  RowNumericProperty,
  RowNumericSubAggregator
} from "common/query-builder/query-entry-section/components/aggregations";
import { BehaviorRowDateRange } from "common/query-builder/query-entry-section/components/behaviors";
import { RowFilters } from "common/query-builder/query-entry-section/components/filters";
import { RowSelector } from "common/query-builder/query-entry-section/components/selector";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import {
  EntryPart
} from "common/query-builder/query-entry-section/types";
import { CustomBucketSubitem } from "common/query-builder/query-entry-section/components/custom-bucket-subitem";
import { StepRangeSelectorEl } from "common/widgets/step-range-selector";
import { ButtonTheme } from "common/components/button/constants";
import { Placement } from "common/components/tooltip/types";
import { ButtonEl } from "common/components/button";
import { getFilterOperatorSymbol, getFilterOperatorTooltip } from "../filter/utils";
import { FilterValueTemplate } from "../filter/types/index";
import { BaseEntryRowEl } from "../base/template";
import { isBehavioralFilterNegated } from "../behavior/utils";
import { SegmentListObjectPropertySelector } from "../segment-entry/components/segment-list-object-property-selector";
const NO_OPERATOR_TYPES = /* @__PURE__ */ new Set([PropertyType.Object, PropertyType.NonProperty]);
export function SuperEntryRowEl({ section, entry, index }) {
  var _a, _b;
  const hasNewUI = section.hasNewQBUI();
  const hasNewCountMenu = section.hasNewCountMenu();
  const displayEntryFilter = this.entryFilter && !section.helpers.hasFirstTimeEventFilter(index) && !isBehavioralFilterNegated(entry);
  const displayEntryAggregations = (entry.type !== ResourceType.Cohort || hasNewCountMenu) && section.attr(`metrics-mode`) && (this.entryFilter ? displayEntryFilter : true);
  const exclusionStepEl = this.hasEventValue() && section.helpers.isExclusionStepsSection() && /* @__PURE__ */ jsx(StepRangeSelectorEl, {
    attrs: {
      selected: JSON.stringify(section.helpers.exclusionSteps.getExclusionStep(index)),
      "num-steps": section.helpers.exclusionSteps.getNumExclusionSteps(),
      "row-label-type": section.helpers.exclusionSteps.getExclusionStepsRowLabelType(),
      theme: SelectTheme.Tertiary,
      label: !hasNewUI && `between`
    },
    on: { select: (ev) => section.helpers.exclusionSteps.onExclusionStepSelected(ev, index) }
  });
  const selectorRenderer = () => {
    var _a2;
    return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(RowSelector, {
      el: section,
      entry,
      index,
      isPlaceholder: !this.hasEventValue()
    }), !hasNewUI && exclusionStepEl, displayEntryAggregations && !hasNewUI && /* @__PURE__ */ jsx(RowNumericAggregator, {
      el: section,
      entry,
      index
    }), !hasNewCountMenu && this.canPerformPropertyAggregation() && /* @__PURE__ */ jsx(RowNumericProperty, {
      el: section,
      entry,
      index,
      isPlaceholder: !this.hasPropertyValue(),
      handleSelect: (property) => this.onEntryPropertySelected(property)
    }), this.canPerformListObjectPropertySelection() && /* @__PURE__ */ jsx(SegmentListObjectPropertySelector, {
      segment: this.property,
      entryRowIdx: index,
      section,
      hideOverflowControls: true,
      handleSelect: (property) => this.handleListObjectPropertySelect(property),
      numericOnly: this.canSelectOnlyNumericProperties()
    }), displayEntryFilter && !NO_OPERATOR_TYPES.has(this.entryFilter.propertyType) && /* @__PURE__ */ jsx(SelectEl, {
      attrs: {
        elref: `filterOperator`,
        "menu-width": 200,
        "menu-height": 400,
        "selected-option": getFilterOperatorSymbol(this.entryFilter),
        theme: SelectTheme.Select,
        tooltip: getFilterOperatorTooltip(this.entryFilter)
      },
      class: {
        [`for-${this.entryFilter.propertyType}`]: true,
        [`for-${kebabCase(this.entryFilter.filterOperator)}-operator`]: true
      },
      props: { sections: this.getEntryFilterOperators() },
      on: { change: (ev) => this.handleEntryFilterOperatorUpdate(ev) }
    }), displayEntryFilter && FilterValueTemplate[this.entryFilter.propertyType] && FilterValueTemplate[this.entryFilter.propertyType]({
      filter: this.entryFilter,
      isActive: section.state.openEntry.index === index && section.state.openEntry.part === EntryPart.FilterValue,
      onChange: (value) => this.handleEntryFilterValueUpdate(`filterValue`, value),
      onSelectMenuChange: (ev) => this.section.helpers.onSelectMenuChange(ev, index, EntryPart.FilterValue),
      setActiveEntry: (state) => this.section.helpers.setActiveEntry(state, { index, part: EntryPart.FilterValue }),
      updateEntry: (filter) => this.updateEntryFilter(filter),
      eventNames: this.hasEventValue() ? [(_a2 = this.entry.event) == null ? void 0 : _a2.value] : [],
      projectId: this.section.attr(`project-id`),
      workspaceId: this.section.attr(`workspace-id`),
      dataGroupId: this.section.attr(`data-group-id`),
      userId: this.section.attr(`user-id`)
    }), section.helpers.isDateRangeType(entry.type) && /* @__PURE__ */ jsx(BehaviorRowDateRange, {
      el: section,
      entry,
      index
    }), displayEntryAggregations && !hasNewUI && /* @__PURE__ */ jsx(RowNumericSubAggregator, {
      el: section,
      entry,
      index
    }));
  };
  const rowFilters = /* @__PURE__ */ jsx(RowFilters, {
    entry,
    eventNames: ((_a = entry.event) == null ? void 0 : _a.value) ? [(_b = entry.event) == null ? void 0 : _b.value] : [],
    filters: section.helpers.getEntryFilters(index)[EntryPart.Filters],
    index,
    onUpdate: (filters, filtersOperator, queryReason) => this.handleFilterSectionUpdate(filters, filtersOperator, queryReason),
    inlineCohortsOverride: section.attr(`inline-cohorts-override`),
    projectHasCohorts: section.attr(`project-has-cohorts`) && section.attr(`allow-cohort-filter`),
    hideEvents: !section.helpers.isEventBasedEntry(entry),
    isBehaviorMode: section.attr(`behavior-mode`),
    isReadOnly: section.attr(`readonly`),
    hasNewUI: section.hasNewQBUI(),
    nestedAttrs: section.helpers.getNestedSectionAttrs(),
    placeholders: section.state.placeholders.get(index),
    onAbortStage: () => section.helpers.setActiveEntry(`close`, { index, part: EntryPart.Filters })
  });
  const bodyRowRenderer = () => {
    return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(CustomBucketSubitem, {
      section,
      index
    }), !hasNewUI && rowFilters, displayEntryAggregations && hasNewUI && !hasNewCountMenu && /* @__PURE__ */ jsx(RowNumericSubAggregator, {
      el: section,
      entry,
      index
    }), displayEntryAggregations && hasNewCountMenu && /* @__PURE__ */ jsx(CountBySelection, {
      el: section,
      entry,
      index
    }), /* @__PURE__ */ jsx(MultiAttributionAggregator, {
      el: section,
      index
    }), section.hasNewQBUI() && exclusionStepEl);
  };
  const suffixRenderer = hasNewUI && (() => rowFilters);
  const actionsRenderer = hasNewUI && section.getRowFilteringCapabilities(this).canApplyFilter && (() => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      theme: ButtonTheme.Tertiary,
      icon: `filter-xs`,
      tooltip: {
        "delay-in": 10,
        "delay-out": 10,
        placement: Placement.Top,
        text: `Filter`
      }
    },
    on: {
      click: () => section.helpers.showPlaceholderForEntry({ part: EntryPart.Filters, index })
    }
  })));
  return /* @__PURE__ */ jsx(BaseEntryRowEl, {
    index,
    el: section,
    entry,
    rowRenderer: { actionsRenderer, selectorRenderer, bodyRowRenderer, suffixRenderer },
    isPlaceholder: this.isPlaceholder()
  });
}
